import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BsWhatsapp, BsPhone, BsGeoAlt, BsCalendarCheck, BsClockHistory, BsGraphUp, BsX } from 'react-icons/bs';
import { BiChevronRight, BiTimeFive, BiUser, BiChat } from 'react-icons/bi';
import CustomLoading from '../../customLoading/CustomLoading';
import { getB2CLeadDetail, updateB2CLeadStatus, getB2CLeadPhoneStats, updateB2CLead } from '../../../services/b2cLeads';
import Dropdown from '../../inputFields/DropDown';
import { statesOfIndia } from '../../../constants/states';
import { districtApi } from '../../../constants/apis';
import axios from 'axios';
import { getUserToken } from '../../../helpers/local_storage_helpers';
import { toast } from 'react-toastify';
import UserOrderHistory from '../../../pages/sales/UserOrderHistory';
import ManualInput from '../../inputFields/ManualInput';
import LeadWhatsappChat from '../b2b/LeadWhatsappChat';

const B2CLeadDetail = ({ id, isAssignmentId = true, leadContext = null, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [districts, setDistricts] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    // Editable fields
    const [location, setLocation] = useState({
        state: '',
        district: '',
        area: '',
        pincode: ''
    });

    const [shopName, setShopName] = useState(leadContext?.shop_name || '');
    const [businessCardFile, setBusinessCardFile] = useState(null);
    const [isSavingRetailer, setIsSavingRetailer] = useState(false);
    const [lightboxUrl, setLightboxUrl] = useState(null);

    useEffect(() => {
        const fetchDetail = async () => {
            setLoading(true);
            try {
                const response = await getB2CLeadDetail(id, isAssignmentId);
                if (response.status === 200) {
                    setData(response.data);
                    // Initialize editable location
                    if (response.data.location) {
                        setLocation({
                            state: response.data.location.state || '',
                            district: response.data.location.district || '',
                            area: response.data.location.area || '',
                            pincode: response.data.location.pincode || ''
                        });
                        if (response.data.location.state) {
                            fetchDistricts(response.data.location.state);
                        }
                    }
                    // Initialize retailer fields
                    setShopName(response.data.lead?.shop_name || response.data.shop_name || response.data.assignment?.shop_name || leadContext?.shop_name || '');
                    setBusinessCardFile(null);
                }
            } catch (error) {
                console.error("Error fetching lead detail:", error);
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchDetail();
    }, [id, isAssignmentId]);

    const fetchDistricts = async (stateName) => {
        try {
            const res = await axios.get(`${districtApi}${stateName}`, {
                headers: { Authorization: `Token ${getUserToken()}` }
            });
            if (res.status === 200) setDistricts(res.data);
        } catch (error) {
            setDistricts([]);
        }
    };

    const handleSaveLocation = async () => {
        setIsSaving(true);
        try {
            const res = await updateB2CLead(data.lead.id, location);
            if (res.status === 200) {
                toast.success("Location updated");
                // Refresh data if needed, but local state is already updated
            }
        } catch (error) {
            toast.error("Failed to update location");
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveRetailer = async () => {
        setIsSavingRetailer(true);
        try {
            const formData = new FormData();
            formData.append('shop_name', shopName);
            if (businessCardFile) {
                formData.append('retailer_bussiness_card', businessCardFile);
            }
            const targetLeadId = data.lead?.id || data.id || data.assignment?.lead_id;
            const res = await updateB2CLead(targetLeadId, formData);
            if (res.status === 200) {
                toast.success("Retailer information updated");
                // Refresh data
                const response = await getB2CLeadDetail(id, isAssignmentId);
                if (response.status === 200) {
                    setData(response.data);
                    setShopName(response.data.lead?.shop_name || response.data.shop_name || response.data.assignment?.shop_name || '');
                    setBusinessCardFile(null);
                }
            }
        } catch (error) {
            console.error("Error updating retailer:", error);
            toast.error("Failed to update retailer info");
        } finally {
            setIsSavingRetailer(false);
        }
    };

    const timeline = useMemo(() => {
        if (!data) return [];
        const calls = (data.call_history || []).map(c => ({
            ...c,
            type: 'call',
            timestamp: new Date(c.date_time).getTime()
        }));
        const assignments = (data.assignment_history || []).map(a => ({
            ...a,
            type: 'assignment',
            timestamp: new Date(a.date).getTime()
        }));
        return [...calls, ...assignments].sort((a, b) => b.timestamp - a.timestamp);
    }, [data]);

    const leadPhone = data?.lead?.phone ?? leadContext?.phone;
    const leadUserId = data?.lead?.user_id ?? leadContext?.user_id;
    const leadIsPaid = data?.lead?.is_paid ?? leadContext?.is_paid;

    if (!id) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[1000] flex justify-end"
                onClick={onClose}
            >
                <motion.div
                    initial={{ x: '100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '100%' }}
                    transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                    className="w-full max-w-2xl bg-gray-50 h-full shadow-2xl flex flex-col overflow-hidden"
                    onClick={e => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="bg-white border-b px-4 py-3 flex justify-between items-center sticky top-0 z-10 shadow-sm">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary">
                                <BiUser size={24} />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-gray-800 leading-tight">
                                    {loading ? 'Loading...' : data?.lead?.name || 'Lead Detail'}
                                </h2>
                                <div className="flex gap-2 items-center">
                                    <p className="text-xs text-gray-500">ID: {id}</p>
                                    {(data?.lead?.shop_name || data?.shop_name || data?.assignment?.shop_name) && (
                                        <>
                                            <span className="text-gray-300">•</span>
                                            <span className="text-xs text-indigo-600 font-bold">Shop: {data.lead?.shop_name || data?.shop_name || data?.assignment?.shop_name}</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
                        >
                            <BsX size={28} />
                        </button>
                    </div>

                    {loading ? (
                        <div className="flex-1 flex items-center justify-center">
                            <CustomLoading size={50} />
                        </div>
                    ) : (
                        <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-10">

                            {/* Visual Status & Actions */}
                            <div className="flex flex-wrap gap-2 items-center">
                                <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${data?.lead?.current_status === 'converted' ? 'bg-green-100 text-green-700' :
                                    data?.lead?.current_status === 'follow_up' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                                    }`}>
                                    {data?.lead?.current_status?.replace('_', ' ')}
                                </span>
                                {leadIsPaid != null ? (
                                    <span
                                        className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                            leadIsPaid ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"
                                        }`}
                                    >
                                        {leadIsPaid ? "Paid customer" : "Unpaid"}
                                    </span>
                                ) : null}
                                <div className="flex-1" />
                                <a
                                    href={data?.lead?.whatsapp}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs font-semibold hover:bg-green-600 transition-all shadow-sm"
                                >
                                    <BsWhatsapp /> WhatsApp
                                </a>
                                <a
                                    href={`tel:${leadPhone || data?.lead?.phone}`}
                                    className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-semibold hover:bg-primary-dark transition-all shadow-sm"
                                >
                                    <BsPhone /> Call Now
                                </a>
                            </div>

                            {(leadPhone || data?.lead?.phone) && <LeadWhatsappChat phone={leadPhone || data?.lead?.phone} />}

                            {/* Follow-up Range Display */}
                            {data?.follow_up?.active && (
                                <div className="bg-amber-50 border border-amber-200 p-3 rounded-xl flex items-center justify-between shadow-sm">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-amber-100 text-amber-600 rounded-lg">
                                            <BsCalendarCheck size={20} />
                                        </div>
                                        <div>
                                            <div className="text-[10px] text-amber-500 font-bold uppercase tracking-wider">Scheduled Follow-up</div>
                                            <div className="text-sm font-bold text-amber-900">
                                                {data.follow_up.start_date} {data.follow_up.end_date ? `to ${data.follow_up.end_date}` : ''}
                                            </div>
                                        </div>
                                    </div>
                                    <span className="px-2 py-0.5 bg-amber-500 text-white text-[10px] font-black rounded-full shadow-sm animate-pulse">ACTIVE</span>
                                </div>
                            )}

                            {/* Metrics Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {[
                                    { label: 'Total Calls', value: data?.metrics?.total_calls, icon: <BsPhone className="text-blue-500" /> },
                                    { label: 'Tracked Talk', value: `${Math.floor(data?.metrics?.tracked_talk_time_seconds / 60)}m`, icon: <BiTimeFive className="text-emerald-500" /> },
                                    { label: 'Untracked Talk', value: `${Math.floor(data?.metrics?.untracked_talk_time_seconds / 60)}m`, icon: <BiChat className="text-gray-400" /> },
                                    { label: 'NPC Count', value: data?.lead?.npc_count, icon: <BiChat className="text-orange-500" /> },
                                    { label: 'Created', value: new Date(data?.lead?.created_at).toLocaleDateString(), icon: <BsCalendarCheck className="text-purple-500" /> },
                                    { label: 'Last Call', value: data?.metrics?.last_called_at ? new Date(data?.metrics?.last_called_at).toLocaleDateString() : 'N/A', icon: <BsClockHistory className="text-indigo-500" /> },
                                    { label: 'Last Update By', value: data?.metrics?.last_updated_by || 'N/A', icon: <BiUser className="text-slate-400" />, fullWidth: true },
                                ].map((item, i) => (
                                    <div key={i} className={`bg-white p-3 rounded-xl border border-gray-100 shadow-sm flex flex-col items-center text-center ${item.fullWidth ? 'col-span-2' : ''}`}>
                                        <div className="text-xl mb-1">{item.icon}</div>
                                        <div className="text-sm font-bold text-gray-800 break-words w-full">{item.value}</div>
                                        <div className="text-[10px] text-gray-500 font-medium uppercase">{item.label}</div>
                                    </div>
                                ))}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Location & Interests */}
                                <div className="space-y-4">
                                    <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col gap-3">
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="text-gray-800 font-bold text-sm flex items-center gap-2">
                                                <BsGeoAlt className="text-red-500" /> Edit Location
                                            </div>
                                        </div>

                                        <div className="space-y-3">
                                            <Dropdown
                                                label="State"
                                                needSearch
                                                options={statesOfIndia.map(s => ({ label: s, value: s }))}
                                                value={location.state}
                                                onChange={(val) => {
                                                    setLocation(prev => ({ ...prev, state: val, district: '' }));
                                                    fetchDistricts(val);
                                                }}
                                                className="text-xs"
                                                compact
                                            />
                                            <Dropdown
                                                label="District"
                                                options={districts.map(d => ({ label: d.name, value: d.name }))}
                                                value={location.district}
                                                onChange={(val) => setLocation(prev => ({ ...prev, district: val }))}
                                                className="text-xs"
                                                compact
                                                needSearch
                                            />
                                            <ManualInput
                                                label="Area / Address Line 1"
                                                value={location.area}
                                                onChange={(e) => setLocation(prev => ({ ...prev, area: e.target.value }))}
                                                className="text-xs"
                                                placeholder="Enter Area / Address"
                                            />
                                            <ManualInput
                                                label="Pincode"
                                                value={location.pincode}
                                                onChange={(e) => setLocation(prev => ({ ...prev, pincode: e.target.value }))}
                                                className="text-xs"
                                                placeholder="Enter Pincode"
                                            />
                                        </div>
                                        <button
                                            onClick={handleSaveLocation}
                                            disabled={isSaving}
                                            className="text-[10px] font-bold text-primary hover:underline disabled:text-gray-400 self-start"
                                        >
                                            {isSaving ? 'Saving...' : 'Save Changes'}
                                        </button>
                                    </div>

                                    {(data?.lead?.current_status === 'retailer' || data?.lead?.lead_status === 'retailer' || data?.lead_status === 'retailer' || data?.lead?.shop_name || data?.shop_name || data?.assignment?.shop_name || data?.lead?.retailer_bussiness_card || data?.retailer_bussiness_card || data?.assignment?.retailer_bussiness_card) && (
                                        <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col gap-3">
                                            <div className="text-gray-800 font-bold text-sm flex items-center gap-2">
                                                <BiUser className="text-indigo-500" /> Edit Retailer Info
                                            </div>
                                            <div className="space-y-3">
                                                <ManualInput
                                                    label="Shop/Business Name"
                                                    value={shopName}
                                                    onChange={(e) => setShopName(e.target.value)}
                                                    className="text-xs"
                                                    placeholder="Enter shop name"
                                                />
                                                <div className="space-y-1">
                                                    <label className="block text-xs font-bold text-gray-500 uppercase">
                                                        Business Card
                                                    </label>
                                                    {(data?.lead?.retailer_bussiness_card || data?.retailer_bussiness_card || data?.assignment?.retailer_bussiness_card) && (
                                                        <div className="mb-2">
                                                            <button
                                                                onClick={() => setLightboxUrl(data.lead?.retailer_bussiness_card || data.retailer_bussiness_card || data.assignment?.retailer_bussiness_card)}
                                                                className="inline-block text-left hover:opacity-80 transition-opacity cursor-zoom-in"
                                                            >
                                                                    <img
                                                                        src={data.lead?.retailer_bussiness_card || data.retailer_bussiness_card || data.assignment?.retailer_bussiness_card}
                                                                        alt="Business Card"
                                                                        className="max-h-24 rounded-lg border object-cover"
                                                                    />
                                                            </button>
                                                        </div>
                                                    )}
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-primary outline-none"
                                                        onChange={(e) => setBusinessCardFile(e.target.files[0] || null)}
                                                    />
                                                </div>
                                            </div>
                                            <button
                                                onClick={handleSaveRetailer}
                                                disabled={isSavingRetailer}
                                                className="text-[10px] font-bold text-primary hover:underline disabled:text-gray-400 self-start"
                                            >
                                                {isSavingRetailer ? 'Saving...' : 'Save Retailer Info'}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                                    <div className="flex items-center gap-2 mb-3 text-gray-800 font-bold text-sm">
                                        <BsGraphUp className="text-blue-500" /> Product Interests
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        {data?.product_interests?.map((p, i) => (
                                            <div key={i} className="flex justify-between items-center px-3 py-2 bg-blue-50/50 text-blue-700 rounded-lg text-[10px] font-bold border border-blue-100">
                                                <span>{p.product}</span>
                                                <span className="text-gray-400 font-medium">{p.date}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Timeline */}
                            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                <div className="px-4 py-3 bg-gray-50 border-b flex items-center gap-2 text-gray-800 font-bold text-sm">
                                    <BsClockHistory className="text-primary" /> Chronological History
                                </div>
                                <div className="divide-y max-h-[400px] overflow-y-auto">
                                    {timeline.map((item, i) => (
                                        <div key={i} className="p-3 hover:bg-gray-50 transition-colors">
                                            <div className="flex justify-between items-start mb-1 text-[10px]">
                                                <div className="flex items-center gap-1.5">
                                                    {item.type === 'call' ? (
                                                        <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 font-bold rounded">CALL</span>
                                                    ) : (
                                                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 font-bold rounded">ASSIGNMENT</span>
                                                    )}
                                                    <span className="text-gray-400 font-medium">
                                                        {new Date(item.timestamp).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-1 text-gray-600 font-semibold">
                                                    <BiUser size={12} /> {item.sales_person}
                                                </div>
                                            </div>
                                            <div className="text-xs font-bold text-gray-700 mb-1 flex items-center gap-1">
                                                <BiChevronRight className="text-primary" />
                                                {item.type === 'call' ? `Outcome: ${item.call_outcome}` : `Type: ${item.assignment_type}`}
                                                {item.duration_seconds > 0 && (
                                                    <span className="text-gray-400 font-normal ml-2">({item.duration_seconds}s)</span>
                                                )}
                                            </div>
                                            {item.note && (
                                                <div className="bg-gray-50 p-2 rounded text-[11px] text-gray-600 border-l-2 border-primary mt-1 italic">
                                                    "{item.note}"
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {(leadUserId || leadPhone) ? (
                                <div className="mt-4 border-t pt-4">
                                    <div className="flex items-center gap-2 mb-4 text-gray-800 font-bold text-sm">
                                        <BsGraphUp className="text-emerald-500" /> Order History
                                        {leadIsPaid ? (
                                            <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                                                Has shop order
                                            </span>
                                        ) : null}
                                    </div>
                                    <UserOrderHistory
                                        user_id={leadUserId || undefined}
                                        mobile_number={leadPhone}
                                        showTopItems={false}
                                    />
                                </div>
                            ) : null}
                        </div>
                    )}
                </motion.div>
            </motion.div>
            {lightboxUrl && (
                <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-in fade-in duration-150" onClick={() => setLightboxUrl(null)}>
                    <div className="relative max-w-2xl max-h-[90vh] bg-white rounded-2xl p-2 shadow-2xl" onClick={e => e.stopPropagation()}>
                        <button
                            onClick={() => setLightboxUrl(null)}
                            className="absolute -top-3 -right-3 bg-white border shadow-md text-gray-500 hover:text-gray-800 rounded-full p-1.5 transition-all active:scale-95 z-10"
                        >
                            <BsX size={20} />
                        </button>
                        <img
                            src={lightboxUrl}
                            alt="Business Card Preview"
                            className="max-w-full max-h-[80vh] rounded-xl object-contain"
                        />
                    </div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default B2CLeadDetail;
